import { useState, useEffect } from "react";
import { MessageSquare, Send, Inbox, Clock, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const timeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const SupportTicketForm = () => {
  const { user, profile, role } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setTickets(data as any[]);
      });
  }, [user]);

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    if (subject.trim().length > 200) {
      toast.error("Subject must be under 200 characters");
      return;
    }
    if (message.trim().length > 2000) {
      toast.error("Message must be under 2000 characters");
      return;
    }
    if (!user) return;
    setSubmitting(true);
    const { data, error } = await supabase.from("support_tickets").insert({
      subject: subject.trim(),
      message: message.trim(),
      priority,
      user_id: user.id,
      user_name: profile?.full_name || null,
      user_email: profile?.email || user.email || null,
      user_role: role || null,
    } as any).select().single();

    setSubmitting(false);
    if (error) {
      toast.error("Failed to submit ticket");
      return;
    }

    // Notify info email about new support log
    try {
      await supabase.functions.invoke("notify-signup", {
        body: {
          type: "ticket",
          data: {
            subject: subject.trim(),
            message: message.trim(),
            priority,
            userName: profile?.full_name || null,
            userEmail: profile?.email || user.email || null,
            userRole: role || null,
          }
        }
      });
    } catch (e) {
      console.error("Failed to send ticket notification email:", e);
    }

    toast.success("Support ticket submitted!");
    setTickets(prev => [data as any, ...prev]);
    setSubject("");
    setMessage("");
    setPriority("normal");
    setShowForm(false);
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      open: "bg-[hsl(var(--app-gold))]/20 text-[hsl(var(--app-gold))]",
      resolved: "bg-[hsl(var(--app-green))]/20 text-[hsl(var(--app-green))]",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${styles[status] || "bg-muted text-muted-foreground"}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header + New Ticket Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[hsl(var(--app-gold))]" /> Support Tickets
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] rounded-lg text-xs font-semibold hover:opacity-90 transition"
        >
          {showForm ? "Cancel" : "New Ticket"}
        </button>
      </div>

      {/* New Ticket Form */}
      {showForm && (
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4 space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
              placeholder="Brief description of your issue"
              className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Priority</label>
            <div className="flex gap-2">
              {["normal", "high", "urgent"].map(p => (
                <button key={p} onClick={() => setPriority(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition capitalize ${
                    priority === p
                      ? "bg-[hsl(var(--app-gold))]/15 text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold))]/30"
                      : "bg-[hsl(var(--app-surface-2))] text-muted-foreground hover:text-foreground"
                  }`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Describe your issue in detail..."
              className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50 resize-none"
            />
            <p className="text-[10px] text-muted-foreground text-right mt-1">{message.length}/2000</p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || !subject.trim() || !message.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] rounded-lg text-xs font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> {submitting ? "Submitting..." : "Submit Ticket"}
          </button>
        </div>
      )}

      {/* Ticket List */}
      <div className="space-y-3">
        {tickets.map((t: any) => (
          <div key={t.id} className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h4 className="text-xs font-semibold text-foreground">{t.subject}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">{timeAgo(t.created_at)}</span>
                  <span className="text-[10px] text-muted-foreground capitalize">· {t.priority}</span>
                </div>
              </div>
              {statusBadge(t.status)}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">{t.message}</p>

            {t.admin_response && (
              <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-3 border-l-2 border-[hsl(var(--app-gold))]">
                <p className="text-[10px] text-[hsl(var(--app-gold))] font-semibold mb-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Admin Response
                </p>
                <p className="text-xs text-foreground">{t.admin_response}</p>
                {t.responded_at && <p className="text-[9px] text-muted-foreground mt-1">{timeAgo(t.responded_at)}</p>}
              </div>
            )}
          </div>
        ))}
        {tickets.length === 0 && !showForm && (
          <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-8 text-center">
            <Inbox className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No tickets yet. Click "New Ticket" to get help.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketForm;
